// tippi/src/pages/carrier/CarrierOrderDetail.js
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useCarrierOrg } from '../../hooks/useCarrierOrg';
import { useCarrierOrder } from '../../hooks/useCarrierOrder';
import { useCarrierMembers } from '../../hooks/useCarrierMembers';
import { useOrgVehicles } from '../../hooks/useOrgVehicles';
import { carrierApi } from '../../firebase/functionsService';
import { STATUS_LABEL, STATUS_TONE, canEditFully, canCancel, canReassign } from '../../utils/carrierOrderStatus';
import { orderTitle } from '../../utils/orderTitle';
import { fullAddress, toDate, dayLabel, timeLabel } from '../../utils/orderCardFormat';
import { massAccountLines } from '../../utils/massAccount';
import { buildCarrierOrderTimeline } from '../../utils/carrierOrderTimeline';
import OrdererPhotos from '../../components/common/OrdererPhotos';
import AssignPanel from './AssignPanel';
import CarrierOrderForm from './CarrierOrderForm';
import Field from './Field';
import { ui, chip } from './carrierStyles';

function CancelForm({ orgId, orderId }) {
  const [reason, setReason] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  async function submit(e) {
    e.preventDefault();
    if (!reason.trim()) { setStatus('invalid'); return; }
    setStatus('saving');
    setMessage('');
    try {
      await carrierApi.cancelCarrierOrder({ orgId, orderId, reason });
      setStatus('idle');
    } catch (err) {
      setStatus('error');
      setMessage(err.message || 'Kunne ikke kansellere ordren');
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      <Field id="x-reason" label="Begrunnelse for kansellering" error={status === 'invalid' ? 'Skriv en kort begrunnelse' : null}>
        <input id="x-reason" style={ui.input} value={reason} onChange={e => setReason(e.target.value)} maxLength={500} />
      </Field>
      {status === 'error' && <p role="alert" style={ui.error}>{message}</p>}
      <button type="submit" style={{ ...ui.btnDanger, marginTop: 12 }} disabled={status === 'saving'}>Kanseller ordre</button>
    </form>
  );
}

const Row = ({ label, children }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={ui.hint}>{label}</div>
    <div>{children}</div>
  </div>
);

export default function CarrierOrderDetail() {
  const { orderId } = useParams();
  const { orgId, isStaff, loading: orgLoading } = useCarrierOrg();
  const { order, loading } = useCarrierOrder(orderId);
  const { members } = useCarrierMembers(orgId);
  const { vehicles } = useOrgVehicles(orgId);
  const [editing, setEditing] = useState(false);

  if (orgLoading || loading) return <p style={ui.hint}>Laster…</p>;
  if (!isStaff) return <p style={ui.hint}>Bare administratorer og dispatchere ser ordrene.</p>;
  if (!order || order.carrierOrgId !== orgId) {
    return <p style={ui.hint}>Fant ikke ordren. <Link to="/transportor/ordre">Tilbake til ordrene</Link></p>;
  }

  const customer = order.customer ?? {};
  const scheduled = toDate(order.scheduledAt);
  const delivered = toDate(order.deliveredAt);
  const nameOf = (uid) => members.find(m => m.id === uid)?.name ?? 'En sjåfør';
  const full = canEditFully(order.status);
  const events = buildCarrierOrderTimeline(order, nameOf);

  return (
    <>
      <p style={ui.hint}><Link to="/transportor/ordre">‹ Ordre</Link></p>
      <div style={{ ...ui.row, justifyContent: 'space-between' }}>
        <h1 style={ui.h1}>{orderTitle(order)}</h1>
        <span style={chip(STATUS_TONE[order.status] ?? 'none')}>{STATUS_LABEL[order.status] ?? order.status}</span>
      </div>

      {editing ? (
        <div style={ui.card}>
          <CarrierOrderForm orgId={orgId} order={order} mode={full ? 'full' : 'noteprice'}
                            onSaved={() => setEditing(false)} onCancel={() => setEditing(false)} />
        </div>
      ) : (
        <div style={ui.card}>
          <Row label="Kunde">
            <strong>{customer.name}</strong>
            {customer.contactName && <span> · {customer.contactName}</span>}
            {customer.phone && <> · <a href={`tel:${customer.phone}`}>{customer.phone}</a></>}
          </Row>
          <Row label="Henting">{fullAddress(order.pickupAddress)}</Row>
          <Row label="Levering">{fullAddress(order.deliveryAddress)}</Row>
          <Row label="Tidspunkt">{scheduled ? `${dayLabel(scheduled)} ${timeLabel(scheduled)}` : 'Ikke satt'}</Row>
          {massAccountLines(order).map(l => <Row key={l.label} label={l.label}>{l.value}</Row>)}
          <OrdererPhotos order={order} />
          {order.agreedPrice != null && <Row label="Avtalt pris">{`${order.agreedPrice.toLocaleString('nb-NO')} kr eks. mva`}</Row>}
          {order.dispatcherNote && <Row label="Notat til sjåfør">{order.dispatcherNote}</Row>}
          {order.pickupActualTons != null && <Row label="Faktisk vekt">{`${String(order.pickupActualTons).replace('.', ',')} tonn`}</Row>}
          {delivered && <Row label="Levert">{`${dayLabel(delivered)} ${timeLabel(delivered)}`}</Row>}
          {order.status === 'cancelled' && order.cancelReason && <Row label="Kansellert fordi">{order.cancelReason}</Row>}
          {(full || order.status === 'in_progress') && (
            <button type="button" style={ui.btnSecondary} onClick={() => setEditing(true)}>
              {full ? 'Rediger' : 'Rediger notat og pris'}
            </button>
          )}
        </div>
      )}

      {canReassign(order.status) && (
        <div style={ui.card}>
          <h2 style={ui.h2}>Tildeling</h2>
          <AssignPanel orgId={orgId} order={order} members={members} vehicles={vehicles} />
        </div>
      )}

      {events.length > 0 && (
        <div style={ui.card}>
          <h2 style={ui.h2}>Hendelser</h2>
          {events.map(e => (
            <div key={e.key} style={ui.listRow}>
              <span>
                <strong>{e.label}</strong>
                {e.detail && <span style={{ display: 'block', ...ui.hint }}>{e.detail}</span>}
              </span>
              <span style={ui.hint}>{`${dayLabel(e.at)} ${timeLabel(e.at)}`}</span>
            </div>
          ))}
        </div>
      )}

      {canCancel(order.status) && (
        <div style={ui.card}>
          <h2 style={ui.h2}>Kanseller</h2>
          <CancelForm orgId={orgId} orderId={order.id} />
        </div>
      )}
    </>
  );
}
